import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { timeZone: "Africa/Johannesburg", day: "2-digit", month: "long", year: "numeric" });
}

function verificationError(error) {
  if (error?.code === "functions/invalid-argument") return "This certificate reference is invalid. Please check the QR code or link.";
  return "Certificate verification is temporarily unavailable. Please try again.";
}

export default function CertificateVerificationPage() {
  const { certificateId } = useParams();
  const [state, setState] = useState({ loading: true, error: "", result: null });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setState({ loading: true, error: "", result: null });
      try {
        const verify = httpsCallable(getFunctions(undefined, "us-central1"), "getCertificateVerification");
        const res = await verify({ certificateId });
        if (!cancelled) setState({ loading: false, error: "", result: res?.data || null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: verificationError(err), result: null });
      }
    }
    if (certificateId) run();
    else setState({ loading: false, error: "No certificate reference provided.", result: null });
    return () => { cancelled = true; };
  }, [certificateId, retry]);

  const { loading, error, result } = state;
  const isValid = Boolean(result?.valid);

  return (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "Arial, sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", maxWidth: 480, width: "100%", padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 13, letterSpacing: 1, textTransform: "uppercase", color: "#8a8f98", marginBottom: 12 }}>
          TPC Performance Centre — Certificate Verification
        </div>

        {loading && <div style={{ color: "#555", padding: "24px 0" }}>Verifying certificate…</div>}

        {!loading && error && (
          <div role="alert" style={{ color: "#b91c1c", padding: "16px 0" }}>
            <div>{error}</div>
            {certificateId && <button type="button" onClick={() => setRetry((value) => value + 1)} style={{ marginTop: 16 }}>Try again</button>}
          </div>
        )}

        {!loading && !error && !isValid && (
          <>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✕</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#b91c1c", marginBottom: 6 }}>Certificate Not Found</div>
            <div style={{ color: "#555" }}>We could not find a certificate matching this code. It may be invalid or revoked.</div>
          </>
        )}

        {!loading && !error && isValid && (
          <>
            <div style={{ fontSize: 40, marginBottom: 8, color: "#16a34a" }}>✓</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#16a34a", marginBottom: 16 }}>Valid Certificate</div>
            <div style={{ textAlign: "left", borderTop: "1px solid #eee", paddingTop: 16 }}>
              <Row label="Learner" value={result.learnerName} />
              <Row label="Programme" value={result.programmeName} />
              <Row label="Date of Completion" value={formatDate(result.completionDate)} />
              {result.fundedBy && <Row label="Funded By" value={result.fundedBy} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 14 }}>
      <span style={{ color: "#8a8f98" }}>{label}</span>
      <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{value}</span>
    </div>
  );
}
